import type { Temporal } from 'temporal-polyfill';
import type { Money } from '@/api/generated/models';
import type {
  AgreementDetails,
  PaymentSchedule,
  ScheduledPayment,
} from '@/api/schemas';
import { formatMoney, orNotAvailable } from '@/lib/display';
import { formatDate } from '@/lib/formatDate';
import { paymentState } from '@/lib/payments';

export type InvoiceViewer = {
  today: Temporal.PlainDate;
  timeZone: string;
  locale: string;
};

export type InvoiceDocument = {
  title: string;
  notice: string | null;
  details: ReadonlyArray<{ label: string; value: string }>;
  lines: ReadonlyArray<{
    vin: string;
    contractNumber: string;
    policy: string;
    net: string;
    markup: string;
    total: string;
  }>;
  totals: { net: string; markup: string; total: string };
};

function sum(amounts: readonly Money[], currency: string): Money {
  return {
    amount: amounts.reduce((total, money) => total + money.amount, 0),
    currency,
  };
}

export function invoiceDocument(
  agreement: AgreementDetails,
  schedule: PaymentSchedule,
  payment: ScheduledPayment,
  viewer: InvoiceViewer,
): InvoiceDocument {
  const currency = payment.amount.currency;
  const netAmounts = payment.lines.map((line) => line.netAmount);
  const markupAmounts = payment.lines.map((line) => line.markupAmount);

  return {
    title: `Payment ${payment.number} of ${schedule.payments.length}`,
    notice:
      payment.status === 'Scheduled' ? 'Scheduled – not yet invoiced' : null,
    details: [
      { label: 'Agreement #', value: agreement.agreementId },
      { label: 'Customer', value: orNotAvailable(schedule.customerName) },
      {
        label: 'Dealer',
        value: `${orNotAvailable(agreement.dealerName)} (${agreement.dealershipId})`,
      },
      {
        label: 'Invoice #',
        value:
          payment.invoiceNumbers.length > 0
            ? payment.invoiceNumbers.join(', ')
            : 'Not yet invoiced',
      },
      { label: 'Invoice Date', value: formatDate(payment.invoiceDate, viewer) },
      { label: 'Due Date', value: formatDate(payment.dueDate, viewer) },
      { label: 'Status', value: paymentState(payment, viewer.today) },
    ],
    lines: payment.lines.map((line) => ({
      vin: line.vin,
      contractNumber: line.contractNumber,
      policy: line.serviceDescription,
      net: formatMoney(line.netAmount),
      markup: formatMoney(line.markupAmount),
      total: formatMoney(sum([line.netAmount, line.markupAmount], currency)),
    })),
    totals: {
      net: formatMoney(sum(netAmounts, currency)),
      markup: formatMoney(sum(markupAmounts, currency)),
      total: formatMoney(sum([...netAmounts, ...markupAmounts], currency)),
    },
  };
}
